import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../context/AuthContext';
import { trackEvent, EVENTS } from '../utils/analytics';
import config from '../config/config';
import '../styles/ArticleDetail.css';

// Import dynamic components for rich article content
import StockCard from '../components/StockCard';

const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Remove the separate stockMetrics state - it's now included directly with the stock data

  // Fetch article data and related articles in one effect
  useEffect(() => {
    const fetchArticleData = async () => {
      try {
        setLoading(true);
        
        // Fetch main article data
        const response = await fetch(`${config.apiUrl}/api/articles/${slug}`);
        
        if (response.status === 404) {
          setError('Artikel nicht gefunden');
          return;
        }
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data || typeof data !== 'object') {
          throw new Error('Invalid article data received');
        }
        
        setArticle(data);
        
        // Track view
        trackEvent(EVENTS.VIEW_ARTICLE_DETAIL, {
          articleId: data.id,
          articleTitle: data.title,
          articleCategory: data.category
        });
        
        // We no longer need separate API calls for related articles and stock metrics
        // as they're now included in the main article response

      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Der Artikel konnte nicht geladen werden. Bitte versuche es später erneut.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticleData();
  }, [slug, navigate]);

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('de-DE', options);
  };

  // Render article content with memoization
  const renderArticleContent = () => {
    if (!article || !article.content) return null;
    
    // Split content by stock card placeholders
    const parts = article.content.split(/(\{\{STOCK_CARD_\d+\}\})/g);
    
    return parts.map((part, index) => {
      const match = part.match(/\{\{STOCK_CARD_(\d+)\}\}/);
      
      if (match) {
        const stockIndex = parseInt(match[1], 10);
        if (article.featured_stocks && stockIndex < article.featured_stocks.length) {
          const stock = article.featured_stocks[stockIndex];
          if (stock && stock.id) {
            // Stock already has metrics attached from backend
            return (
              <div key={`stock-${index}`} className="article-stock-card">
                <StockCard 
                  stock={stock}
                  isPortfolio={false}
                  showActions={!!token}
                  sortBy="current_price"
                  initialExpanded={false}
                />
              </div>
            );
          }
        }
        // Placeholder for missing stock
        return (
          <div key={`stock-${index}`} className="article-stock-card-placeholder">
            <p>Aktieninformationen derzeit nicht verfügbar</p>
          </div>
        );
      }
      
      // Content part as HTML
      return part ? (
        <div 
          key={`content-${index}`} 
          className="article-text-section"
          dangerouslySetInnerHTML={{ __html: part }} 
        />
      ) : null;
    });
  };
  
  // Memoize the rendered content to prevent unnecessary re-renders
  const memoizedArticleContent = useMemo(() => renderArticleContent(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [article?.content, article?.featured_stocks, token]);

  if (loading) {
    return (
      <div className="article-loading" role="status">
        <div className="loading-spinner"></div>
        <p>Artikel wird geladen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="article-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{error}</p>
        <Link to="/articles" className="back-to-articles">
          Zurück zur Artikelübersicht
        </Link>
      </div>
    );
  }

  if (!article) return null;

  return (
    <>
      <Helmet>
        <title>{article.title} | DynaStoxx</title>
        <meta name="description" content={article.meta_description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://dynastoxx.com/articles/${article.slug}`} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.meta_description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://dynastoxx.com/articles/${article.slug}`} />
        {article.thumbnail_url && <meta property="og:image" content={article.thumbnail_url} />}
        
        {/* Article specific metadata */}
        <meta property="article:published_time" content={new Date(article.published_date).toISOString()} />
        <meta property="article:modified_time" content={new Date(article.updated_date).toISOString()} />
        <meta property="article:author" content={article.author} />
        <meta property="article:section" content={article.category} />
        {article.tags && article.tags.map(tag => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}
        
        {/* JSON-LD structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.meta_description,
            "image": article.thumbnail_url,
            "datePublished": new Date(article.published_date).toISOString(),
            "dateModified": new Date(article.updated_date).toISOString(),
            "author": {
              "@type": "Person",
              "name": article.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "DynaStoxx",
              "logo": {
                "@type": "ImageObject",
                "url": "https://dynastoxx.com/logo.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://dynastoxx.com/articles/${article.slug}`
            }
          })}
        </script>
      </Helmet>

      <div className="article-detail-container">
        <nav className="article-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Start</Link>
          <i className="fas fa-chevron-right" aria-hidden="true"></i>
          <Link to="/articles">Analysen</Link>
          <i className="fas fa-chevron-right" aria-hidden="true"></i>
          <span aria-current="page">{article.title}</span>
        </nav>

        <article className="article-content">
          <header className="article-header">
            {article.thumbnail_url && (
              <div className="article-thumbnail">
                <img 
                  src={article.thumbnail_url} 
                  alt={article.title}
                  loading="lazy"
                />
              </div>
            )}
            
            <div className="article-meta">
              <span className="article-category">{String(article.category || '').replace(/[-_]/g, ' ')}</span>
              <h1 className="article-title">{article.title}</h1>
              <div className="article-details">
                <span className="article-date">{formatDate(article.published_date)}</span>
                <span className="article-author">Von {article.author}</span>
                <span className="article-views"><i className="fas fa-eye" aria-hidden="true"></i> {article.view_count} Aufrufe</span>
              </div>
            </div>
          </header>

          <div className="article-body">
            <div className="article-text">
              {memoizedArticleContent}
            </div>
          </div>

          <footer className="article-footer">
            <div className="article-tags">
              {article.tags && article.tags.map((tag, index) => (
                <span key={index} className="article-tag">#{tag}</span>
              ))}
            </div>
            
            <div className="article-share">
              <span>Teilen: </span>
              <a 
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(`https://dynastoxx.com/articles/${article.slug}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-button twitter"
                aria-label="Auf X teilen"
                onClick={() => trackEvent(EVENTS.SHARE_ARTICLE, { platform: 'twitter', articleId: article.id })}
              >
                <i className="fab fa-x-twitter" aria-hidden="true"></i>
              </a>
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://dynastoxx.com/articles/${article.slug}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-button facebook"
                aria-label="Auf Facebook teilen"
                onClick={() => trackEvent(EVENTS.SHARE_ARTICLE, { platform: 'facebook', articleId: article.id })}
              >
                <i className="fab fa-facebook-f" aria-hidden="true"></i>
              </a>
              <a 
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://dynastoxx.com/articles/${article.slug}`)}&title=${encodeURIComponent(article.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-button linkedin"
                aria-label="Auf LinkedIn teilen"
                onClick={() => trackEvent(EVENTS.SHARE_ARTICLE, { platform: 'linkedin', articleId: article.id })}
              >
                <i className="fab fa-linkedin-in" aria-hidden="true"></i>
              </a>
            </div>
          </footer>
        </article>

        {article.related_articles && article.related_articles.length > 0 && (
          <div className="related-articles">
            <h2>Ähnliche Artikel</h2>
            <div className="related-articles-grid">
              {article.related_articles.map(relatedArticle => (
                <Link 
                  to={`/articles/${relatedArticle.slug}`} 
                  className="related-article-card"
                  key={relatedArticle.id}
                  onClick={() => trackEvent(EVENTS.CLICK_RELATED_ARTICLE, {
                    fromArticleId: article.id,
                    toArticleId: relatedArticle.id
                  })}
                >
                  {relatedArticle.thumbnail_url && (
                    <div className="related-article-image">
                      <img 
                        src={relatedArticle.thumbnail_url} 
                        alt={relatedArticle.title}
                        loading="lazy" 
                      />
                    </div>
                  )}
                  <div className="related-article-info">
                    <h3>{relatedArticle.title}</h3>
                    <span className="related-article-date">{formatDate(relatedArticle.published_date)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="article-cta">
          <h3>Bereit zur Investition?</h3>
          <p>Finde die besten Aktien nach deinen eigenen Kriterien.</p>
          <div className="article-cta-buttons">
            <Link to="/search" className="cta-button primary">
              <i className="fas fa-search"></i> Aktiensuche starten
            </Link>
            {!token && (
              <Link to="/register" className="cta-button secondary">
                <i className="fas fa-user-plus"></i> Konto erstellen
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ArticleDetail;